import { useEffect, useRef } from 'react';
import { IAd, IAdTrackingEvent, PlayerBase, IAdBreakStartedEvent } from '@24i/player-base';
import { isPlatformWeb, isPlatformIos, isPlatformTvos } from '@24i/nxg-core-utils';
import { onAnalytics, ANALYTICS_TRIGGERS } from '@24i/nxg-sdk-smartott-shared/src/analytics';
import { PlaybackStarted } from '@24i/nxg-core-utils/src/globalSingletons';
import { isCompatibleSource } from '@24i/player-utils-react';
import { PlaybackState, updatePlaybackState } from './playbackState';
import { useBuildPlaybackEvent } from './buildPlaybackEvent';
import { useTriggerOnPlaybackHeartbeat } from './useTriggerOnPlaybackHeartbeat';
import { useTriggerOnPlaybackMilestone } from './useTriggerOnPlaybackMilestone';
import { useTriggerOnAdSkipped } from './useTriggerOnAdSkipped';
import { useTriggerOnAdBuffering } from './useTriggerOnAdBuffering';
import { useTriggerOnAdComplete, isAdPlaybackCompleted } from './useTriggerOnAdComplete';
import { useTriggerOnAdStart } from './useTriggerOnAdStart';
import { useTriggerOnAdError } from './useTriggerOnAdError';
import { useTriggerOnAdStop } from './useTriggerOnAdStop';
import { useTriggerOnPlayerEvent } from './useTriggerOnPlayerEvent';
import { PlayerAnalytics } from '../../types';

const debounceEventInterval = 2000;

export const usePlayerAnalytics = (props: PlayerAnalytics) => {
    const playerInstance = useRef<PlayerBase>();
    const currentPlaybackState = useRef<PlaybackState | undefined>(undefined);
    const startBufferingTimeRef = useRef<number>(0);
    const endBufferingTimeRef = useRef<number>(0);
    const currentAdRef = useRef<IAd | null>(null);
    const currentAdBreakRef = useRef<IAdBreakStartedEvent | null>(null);
    const currentAdPlaybackCompleteRef = useRef<boolean>(false);
    const currentAdBuferringTimeref = useRef<number>(0);

    const nextPlaybackState = useRef<PlaybackState>({});
    nextPlaybackState.current = {
        asset: props?.asset,
        stream: props?.stream,
        source: props?.source,
        seriesData: props?.seriesData,
        hasAccurateTimes: false,
    };

    const autoPause = useRef<boolean>(false);

    const buildPlaybackEvent = useBuildPlaybackEvent();

    const initOrUpdatePlaybackState = () => {
        if (
            !currentPlaybackState.current ||
            currentPlaybackState.current.asset?.id !== nextPlaybackState.current.asset?.id ||
            !isCompatibleSource(
                currentPlaybackState.current.source,
                nextPlaybackState.current.source
            )
        ) {
            currentPlaybackState.current = nextPlaybackState.current;
        }
        updatePlaybackState(currentPlaybackState.current, playerInstance.current);
    };

    const triggerOnPlaybackHeartbeat = useTriggerOnPlaybackHeartbeat(buildPlaybackEvent);
    const triggerOnPlaybackMilestone = useTriggerOnPlaybackMilestone(buildPlaybackEvent);
    const triggerOnAdBuffering = useTriggerOnAdBuffering(buildPlaybackEvent); // AD_BUFERRING
    const triggerOnAdSkipped = useTriggerOnAdSkipped(buildPlaybackEvent);
    const triggerOnAdComplete = useTriggerOnAdComplete(buildPlaybackEvent);
    const triggerOnAdStart = useTriggerOnAdStart(buildPlaybackEvent);
    const triggerOnAdError = useTriggerOnAdError(buildPlaybackEvent);
    const triggerOnAdStop = useTriggerOnAdStop(buildPlaybackEvent);
    const triggerOnPlayerEvent = useTriggerOnPlayerEvent(buildPlaybackEvent);

    useEffect(() => {
        if (isPlatformWeb) {
            const handleAppStateChange = () => {
                if (PlaybackStarted.get()) {
                    const event = buildPlaybackEvent('APP_CLOSE', currentPlaybackState.current);
                    if (event) onAnalytics(ANALYTICS_TRIGGERS.APP_CLOSE, event);
                    PlaybackStarted.set(false);
                }
            };
            window.addEventListener('beforeunload', handleAppStateChange);
            return () => {
                window.removeEventListener('beforeunload', handleAppStateChange);
            };
        }
        return () => {};
    }, []);

    const resetAdStates = () => {
        currentAdRef.current = null;
        currentAdPlaybackCompleteRef.current = false;
    };

    const checkAndSendAdStopEvent = () => {
        // Check if current AdRef is present
        if (playerInstance.current?.currentAd) {
            const ad = playerInstance.current?.currentAd;
            triggerOnAdStop(currentPlaybackState.current, {
                adId: ad?.adId,
                adName: ad?.title,
                adPosition: currentAdBreakRef.current?.adBreakOffset,
                adDuration: ad?.duration,
            });
        }
    };

    const onTimeUpdate = () => {
        updatePlaybackState(currentPlaybackState.current, playerInstance.current);
        // Check if current AdRef is present
        if (playerInstance.current?.currentAd !== null) {
            // check
            if (
                isAdPlaybackCompleted(currentPlaybackState.current) &&
                !currentAdPlaybackCompleteRef.current
            ) {
                const ad = currentAdRef.current;
                triggerOnAdComplete(currentPlaybackState.current, {
                    adId: ad?.adId,
                    adName: ad?.title,
                    adPosition: currentAdBreakRef.current?.adBreakOffset,
                    adDuration: ad?.duration,
                });
                currentAdPlaybackCompleteRef.current = true;
            }
        } else {
            triggerOnPlaybackHeartbeat(currentPlaybackState.current);
            triggerOnPlaybackMilestone(currentPlaybackState.current);
        }
    };

    const onPlay = () => {
        if (autoPause.current) {
            autoPause.current = false;
        } else {
            props.onPlay?.();
            initOrUpdatePlaybackState();
            triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_UNPAUSE');
            triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_START');
        }
    };

    const onAutoPause = () => {
        autoPause.current = true;
    };

    const onPause = () => {
        if (autoPause.current) return;
        props.onPause?.();
        updatePlaybackState(currentPlaybackState.current, playerInstance.current);
        triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_PAUSE');
    };

    const onEnd = () => {
        props.onEnd?.();
        checkAndSendAdStopEvent();
        triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_ENDED');
    };

    const onStop = () => {
        props.onStop?.();
        checkAndSendAdStopEvent();
        triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_STOP');
    };

    const onWaiting = () => {
        updatePlaybackState(currentPlaybackState.current, playerInstance.current);
    };

    const onSeeked = () => {
        if (Date.now() - endBufferingTimeRef?.current <= debounceEventInterval) return;
        endBufferingTimeRef.current = Date.now();

        triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_BUFFERING_FINISHED');
    };

    const onSeeking = () => {
        if (Date.now() - startBufferingTimeRef?.current <= debounceEventInterval) return;
        startBufferingTimeRef.current = Date.now();

        triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_BUFFERING_START');
    };

    const onCanPlay = () => {
        initOrUpdatePlaybackState();
        if ((isPlatformIos || isPlatformTvos) && playerInstance.current?.playing) {
            triggerOnPlayerEvent(currentPlaybackState.current, 'PLAYBACK_START');
        }
    };

    const onAdBreakEnded = () => {
        resetAdStates();
    };

    const onAdStarted = (ad: IAd) => {
        updatePlaybackState(currentPlaybackState.current, playerInstance.current);

        // setCurrent Ad
        currentAdRef.current = ad;
        const { title, adId, duration } = ad;

        // trigger pending ad_buffer event
        triggerOnAdBuffering(currentPlaybackState.current, {
            adId,
            adName: title,
            adPosition: currentAdBreakRef.current?.adBreakOffset,
            adDuration: duration,
            timestamp: currentAdBuferringTimeref.current,
        });

        // trigger ad_start event
        triggerOnAdStart(currentPlaybackState.current, {
            adId,
            adName: title,
            adDuration: duration,
            adPosition: currentAdBreakRef.current?.adBreakOffset,
        });
    };

    const onAdBreaksStarted = (currentAdBreak: IAdBreakStartedEvent) => {
        currentAdBreakRef.current = currentAdBreak;
        // mark the timestamp to send the adBuffering event
        currentAdBuferringTimeref.current = Date.now();
    };

    const onAdError = () => {
        const adPosition = currentAdBreakRef.current?.adBreakOffset || 0;
        let adName = '';
        let adBreakId = '';

        if (currentAdRef.current) {
            const { title, adId = '' } = currentAdRef.current;
            adBreakId = adId;
            adName = title;
        }

        triggerOnAdError(currentPlaybackState.current, {
            adId: adBreakId,
            adName,
            adPosition,
        });
        resetAdStates();
    };

    const onAdTracking = (adTrackingEvent: IAdTrackingEvent) => {
        updatePlaybackState(currentPlaybackState.current, playerInstance.current);

        if (adTrackingEvent != null) {
            const { ad, trackingEvent } = adTrackingEvent;
            if (trackingEvent === 'skip') {
                triggerOnAdSkipped(currentPlaybackState.current, {
                    adId: ad?.adId,
                    adName: ad?.title,
                    adPosition: currentAdBreakRef.current?.adBreakOffset,
                    adDuration: ad?.duration,
                });
                resetAdStates();
            }
        }
    };

    const addListeners = (): void => {
        // @ts-ignore
        playerInstance.current?.addEventListener('autopause', onAutoPause);
        playerInstance.current?.addEventListener('timeupdate', onTimeUpdate);
        playerInstance.current?.addEventListener('waiting', onWaiting);
        playerInstance.current?.addEventListener('canplay', onCanPlay);
        playerInstance.current?.addEventListener('adstarted', onAdStarted);
        playerInstance.current?.addEventListener('adbreakended', onAdBreakEnded);
        playerInstance.current?.addEventListener('adbreakstarted', onAdBreaksStarted);
        playerInstance.current?.addEventListener('aderror', onAdError);
        playerInstance.current?.addEventListener('adtracking', onAdTracking);
        playerInstance.current?.addEventListener('seeked', onSeeked);
        playerInstance.current?.addEventListener('seeking', onSeeking);
    };

    const setVideoPlayerRef: PlayerAnalytics['setVideoPlayerRef'] = (ref: PlayerBase) => {
        if (!playerInstance.current) {
            playerInstance.current = ref;
            if (isPlatformWeb) addListeners();
        }
        return props.setVideoPlayerRef?.(ref);
    };

    useEffect(() => {
        if (!isPlatformWeb) addListeners();
        return () => {
            // @ts-ignore
            playerInstance.current?.removeEventListener('autopause', onAutoPause);
            playerInstance.current?.removeEventListener('timeupdate', onTimeUpdate);
            playerInstance.current?.removeEventListener('waiting', onWaiting);
            playerInstance.current?.removeEventListener('canplay', onCanPlay);
            playerInstance.current?.removeEventListener('adstarted', onAdStarted);
            playerInstance.current?.removeEventListener('adbreakended', onAdBreakEnded);
            playerInstance.current?.removeEventListener('adbreakstarted', onAdBreaksStarted);
            playerInstance.current?.removeEventListener('aderror', onAdError);
            playerInstance.current?.removeEventListener('adtracking', onAdTracking);
            playerInstance.current?.removeEventListener('seeked', onSeeked);
            playerInstance.current?.removeEventListener('seeking', onSeeking);
        };
    }, [playerInstance.current]);

    return { onPlay, onPause, onEnd, onStop, setVideoPlayerRef };
};
